import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { useSessionStore } from "@/store/session";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Economics } from "@/pages/admin/Economics";
import { UsagePage } from "@/pages/admin/UsagePage";
import { ProductsPage } from "@/pages/admin/ProductsPage";
import { LLMConfigPage } from "@/pages/admin/LLMConfigPage";
import { AppConfigPage } from "@/pages/admin/AppConfigPage";
import { FlagsPage } from "@/pages/admin/FlagsPage";
import { FreeTierPage } from "@/pages/admin/FreeTierPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { UserDetailPage } from "@/pages/admin/UserDetailPage";
import { FeedbackPage } from "@/pages/admin/FeedbackPage";
import { GenerationsPage } from "@/pages/admin/GenerationsPage";
import { AuditLogPage } from "@/pages/admin/AuditLogPage";
import { LoginPage } from "./LoginPage";
import { AuthCallbackPage } from "./AuthCallbackPage";

/** DUE-221 (Task 12): UX-only gate for the standalone browser-admin bundle.
 * Mirrors `RequireAdmin` in web/src/app/routes.tsx, but redirects to
 * `/login` (this bundle's magic-link screen) instead of `/home` — there is
 * no Telegram/iOS product surface to fall back to here. The server still
 * enforces auth via `require_admin`; this only prevents the dashboard
 * shell from rendering before a session exists. */
function RequireAdminWeb(): JSX.Element {
  const hydrated = useSessionStore((s) => s.hydrated);
  const user = useSessionStore((s) => s.user);
  if (!hydrated) return <FullScreenLoader />;
  if (!user || user.is_admin !== true) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function AdminRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth" element={<AuthCallbackPage />} />
      <Route element={<RequireAdminWeb />}>
        <Route element={<AdminLayout />}>
          {/* Identical /admin/* paths as web/src/app/routes.tsx:138-149 so
              AdminTabs' absolute links keep working. */}
          <Route path="/admin" element={<Navigate to="/admin/economics" replace />} />
          <Route path="/admin/economics"   element={<Economics />} />
          <Route path="/admin/usage"       element={<UsagePage />} />
          <Route path="/admin/products"    element={<ProductsPage />} />
          <Route path="/admin/llm-config"  element={<LLMConfigPage />} />
          <Route path="/admin/app-config"  element={<AppConfigPage />} />
          <Route path="/admin/flags"       element={<FlagsPage />} />
          <Route path="/admin/free-tier"   element={<FreeTierPage />} />
          <Route path="/admin/users"       element={<UsersPage />} />
          <Route path="/admin/users/:userId" element={<UserDetailPage />} />
          <Route path="/admin/feedback"    element={<FeedbackPage />} />
          <Route path="/admin/generations" element={<GenerationsPage />} />
          <Route path="/admin/audit-log"   element={<AuditLogPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/admin/economics" replace />} />
    </Routes>
  );
}
